import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useCreateApplicationEmail } from '~/core/shared/api/middle-office/application';
import { FormField, Input, Wysiwyg } from '~/core/shared/components';
import UnsavedModal from '~/core/shared/components/UnsavedModal.component';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { EmailCreateFormValues } from '~/core/shared/types';
import { useMiddleOfficeConfig } from '../../contexts/middleOfficeConfig';

type EmailCreateProps = {
  documentId: string;
  documentDefinition: string;
  cancel: () => void;
};

const validationSchema: yup.ObjectSchema<EmailCreateFormValues> = yup.object({
  bcc: yup
    .string()
    .test('emails', 'Invalid email addresses', (value) =>
      value
        ? value
            .split(',')
            .every((email) => yup.string().trim().email().isValidSync(email))
        : true,
    ),
  content: yup.string().trim().required(),
  subject: yup.string().trim().required(),
});

export const EmailCreate: FC<EmailCreateProps> = ({
  cancel,
  documentId,
  documentDefinition,
}) => {
  const unsavedChangesModalApi = useOpenClose();
  const middleOfficeConfig = useMiddleOfficeConfig();

  const defaultValues: EmailCreateFormValues = {
    content: '',
    subject: '',
  };

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<EmailCreateFormValues>(validationSchema);
  const form = useForm<EmailCreateFormValues>({
    defaultValues,
    resolver,
  });

  const { createEmail } = useCreateApplicationEmail(
    documentId,
    documentDefinition,
  );

  const onSubmit = useEventHandler(
    async (formValues: EmailCreateFormValues): Promise<void> => {
      await createEmail(formValues);
      form.reset(defaultValues);
      cancel();
    },
  );

  const handleCloseAndReset = useEventHandler(() => {
    form.reset(defaultValues);
    unsavedChangesModalApi.onClose();
    cancel();
  });

  const handleClose = useEventHandler(() => {
    if (form.formState.isDirty) {
      unsavedChangesModalApi.onOpen();
    } else {
      handleCloseAndReset();
    }
  });

  return (
    <Flex direction="column" maxW="342px" minH="40vh">
      <Text my={5}>
        Send a message to the applicant via email and record as a message
        against the application.
      </Text>

      <FormProvider {...form}>
        <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            component={Input}
            label="BCC recipient"
            name="bcc"
            placeholder="Enter comma separated email addresses"
            variant="outline"
          />
          <FormField
            component={Input}
            label="Subject"
            mb={5}
            name="subject"
            required
            variant="outline"
          />

          <FormField
            borderColor="gray.300"
            component={Wysiwyg}
            label="Message text"
            name="content"
            required
            size="md"
            variant="outline"
          />

          <Stack direction="row" justifyContent="end" mt={3}>
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={handleClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              type="submit"
              variant="solid"
            >
              Send to{' '}
              {middleOfficeConfig.getLabel('messageTab', 'mailTo', 'customer')}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
      <UnsavedModal
        close={handleCloseAndReset}
        modalApi={unsavedChangesModalApi}
      />
    </Flex>
  );
};
